import { Avatar } from "../components/Avatar";
import { CARD_SHADOW } from "../components/Card";
import { CitationGrid } from "../components/Citation";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
const wrapperVariants = cva("flex flex-col @container @xs:flex-row", {
    variants: {
        messageType: {
            agent: "pr-0",
            me: "pl-9",
            user: "pr-9",
        },
    },
    defaultVariants: {
        messageType: "agent",
    },
});
const messageVariants = cva("flex rounded-2xl max-w-full", {
    variants: {
        type: {
            user: "gap-2 w-fit",
            agent: "w-full gap-3 flex-col",
        },
    },
    defaultVariants: {
        type: "agent",
    },
});
// This component should only contain padding (inside the bubble).
// Any margin (inter-message spacing) should live outside of Sparkle.
/**
 * The wrapper for one turn of a chat thread, aligning and styling user vs.
 * agent messages. Compose it with ConversationMessageAvatar,
 * ConversationMessageTitle, and ConversationMessageContent to render a full
 * conversation between people and agents; set both messageType and type so
 * messages are styled and aligned correctly.
 * @summary Chat message turn wrapper.
 */
export const ConversationMessageContainer = React.forwardRef(({ children, className, messageType, type, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn(wrapperVariants({ messageType })) },
        React.createElement("div", { className: cn(messageVariants({ type, className })), ...props }, children)));
});
ConversationMessageContainer.displayName = "ConversationMessageContainer";
/** Body of a chat message — the message text (e.g. Markdown) plus an optional citations grid. */
export const ConversationMessageContent = React.forwardRef(({ children, citations, className, type, reversed, showContent = true, ...props }, ref) => {
    return (React.createElement(React.Fragment, null,
        type === "user" && citations && citations.length > 0 && (React.createElement(CitationGrid, { reversed: reversed }, citations)),
        showContent && (React.createElement("div", { ref: ref, className: cn("flex min-w-0 flex-col gap-1", type === "user" &&
                cn("rounded-2xl border border-border bg-muted-background px-3 py-2", CARD_SHADOW), className), ...props },
            React.createElement("div", { className: "min-w-0 break-words text-base text-foreground" }, children),
            type === "agent" && citations && citations.length > 0 && (React.createElement(CitationGrid, null, citations))))));
});
ConversationMessageContent.displayName = "ConversationMessageContent";
/** Sender avatar for a chat message, rounded for users and squared for agents. */
export const ConversationMessageAvatar = React.forwardRef(({ avatarUrl, isBusy, isDisabled, name = "", className, type, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("conversation:p-0 flex gap-2", className), ...props },
        React.createElement(Avatar, { name: name, visual: avatarUrl, busy: isBusy, disabled: isDisabled, isRounded: type === "user", size: "xs" })));
});
ConversationMessageAvatar.displayName = "ConversationMessageAvatar";
/** Header line of a chat message: sender name, timestamp, optional infoChip and completionStatus. */
export const ConversationMessageTitle = React.forwardRef(({ name = "", timestamp, infoChip, completionStatus, renderName, className, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("inline-flex flex-1 items-center justify-between gap-0.5", className), ...props },
        React.createElement("div", { className: "inline-flex items-baseline gap-2 text-foreground" },
            React.createElement("span", { className: "text-sm font-medium" }, renderName(name)),
            React.createElement("span", { className: "text-xs text-muted-foreground" }, timestamp),
            infoChip && (React.createElement("div", { className: "inline-flex self-center" }, infoChip))),
        React.createElement("div", { className: "ml-1 inline-flex items-center" }, completionStatus !== null && completionStatus !== void 0 ? completionStatus : null)));
});
ConversationMessageTitle.displayName = "ConversationMessageTitle";
//# sourceMappingURL=ConversationMessages.js.map